import { Anchor, Box, BoxProps, Button, Card, Flex, Text } from "@mantine/core";
import { useStyles } from "./PurchaseDetailSection.styled";
import { persistUTMQueryParamsToUrlObject } from "@common/utils/router";
import { useRouter } from "next/router";
import Link from "next/link";
import { formatDate } from "@utils/date";
import { paymentMethodsStore } from "@stores/payment-methods/store";
import { isDevPlan, isEnterprisePlan } from "@common/utils/plan/utils";
import { SUBSCRIPTION_STATUS_CANCELLATION_PENDING } from "@stores/subscriptions/constants";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText/ButtonAsText";
import { purchasesStore } from "@stores/purchases/store";
import ErrorMessage from "@common/components/text/ErrorMessage/ErrorMessage";
import { formatPrice } from "@common/utils/string";
import { NUMBER_FORMAT_PRICE_OPTIONS } from "@common/utils/string/constants";
import { convertToNumber } from "@stores/plans/utils";
import { getPrettyPlanTier } from "@stores/purchases/utils";
import { IPurchase } from "@stores/purchases/types";
import {
  PurchaseContextProvider,
  useAddOnPurchaseLineItems,
  useBillsOnUsage,
  usePurchaseContext,
} from "@addons/hooks";
import { LineItemRow } from "@addons/components/LineItemRow";
import { clamp } from "@mantine/hooks";
import { ReactNode } from "react";

function LineItems(boxProps: BoxProps) {
  const { classes } = useStyles();
  const { purchase } = usePurchaseContext();
  const { product, user_count } = purchase;
  const { plan } = product;

  const lineItems = useAddOnPurchaseLineItems();

  const perUserPrice =
    plan.per_user_price && convertToNumber(plan.per_user_price);
  const paidUserCount = clamp(
    (user_count ?? 0) - (plan.users_included ?? 0),
    0,
    Infinity,
  );

  return (
    <Box component="ul" {...boxProps} className={classes.list}>
      {plan.price != null && <LineItemRow label="Plan" total={plan.price} />}
      {!!user_count && (
        <LineItemRow
          label="Users"
          quantity={
            <>
              {user_count}
              {plan.users_included &&
                perUserPrice &&
                ` (${plan.users_included} included)`}
            </>
          }
          total={perUserPrice ? formatPrice(paidUserCount * perUserPrice) : ""}
        />
      )}
      {lineItems.map(({ productType, title, detail, price }) => (
        <LineItemRow
          key={productType}
          label={title}
          quantity={detail}
          total={price}
        />
      ))}
    </Box>
  );
}

function PurchaseDetail({ extra }: { extra?: React.ReactNode }) {
  const { classes } = useStyles();
  const router = useRouter();

  // purchase & payment
  const { purchase, isYearly } = usePurchaseContext();
  const { data: paymentMethod } = paymentMethodsStore.useDefaultPaymentMethod();
  const { product, next_charge, subscription } = purchase;
  const { plan } = product;
  const isEnterprise = isEnterprisePlan(plan.alias);
  const isDev = isDevPlan(plan.alias);
  const planTier = getPrettyPlanTier(plan.alias);
  const isCancellationPending =
    subscription.status === SUBSCRIPTION_STATUS_CANCELLATION_PENDING;
  // hide the invoice total for usage-based add-ons, as it can show a misleading value (GRO-570).
  const showNextPaymentAmount = !useBillsOnUsage();

  // reactivate
  const {
    isPending: loadingReactivatePurchase,
    error: reactivatePurchaseError,
    mutate: reactivatePurchase,
  } = purchasesStore.useReactivatePurchase(purchase.id);

  const footerElements: JSX.Element[] = [];
  if (next_charge) {
    footerElements.push(
      <Flex key="Next charge" fz="sm" align="center">
        <Anchor
          component={Link}
          href={persistUTMQueryParamsToUrlObject(
            router,
            `/account/manage/billing`,
          )}
        >
          Next payment
        </Anchor>
        <Text>
          <Text span px={4}>
            -
          </Text>
          {formatDate(next_charge.date, "short")}
        </Text>
        {paymentMethod && (
          <>
            <Text ml="sm" c="gray.4">
              {paymentMethod.type} {paymentMethod.last4}
            </Text>
            {showNextPaymentAmount && (
              <Text ml="auto" size="lg">
                {formatPrice(
                  convertToNumber(next_charge.amount_due),
                  NUMBER_FORMAT_PRICE_OPTIONS,
                )}
              </Text>
            )}
          </>
        )}
      </Flex>,
    );
  }
  if (isCancellationPending && subscription.current_period_end) {
    footerElements.push(
      <Text key="Pending cancellation" c="gray.4" fz="sm">
        You can continue to use this instance until{" "}
        <Text span fw={700} c="gray.5">
          {formatDate(subscription.current_period_end, "long")}
        </Text>
        .
      </Text>,
    );
  }

  return (
    <Card withBorder className={classes.card}>
      <Card.Section px="lg" py="md">
        <Flex align="baseline">
          <Text fw={700}>
            {planTier && `${planTier} Plan`}
            {isYearly && " (Yearly)"}
          </Text>

          {isCancellationPending && (
            <Text tt="uppercase" fz="xs" fw={700} ml="sm">
              Canceled
            </Text>
          )}

          <Box ml="auto">
            {!isEnterprise &&
              (isCancellationPending ? (
                <ButtonAsText
                  loading={loadingReactivatePurchase}
                  onClick={() => reactivatePurchase(null)}
                  fz="sm"
                >
                  Reactivate
                </ButtonAsText>
              ) : (
                !isDev && (
                  <Button
                    component={Link}
                    fz="sm"
                    px="sm"
                    h="2rem"
                    href={persistUTMQueryParamsToUrlObject(
                      router,
                      `/account/${purchase.id}/plan`,
                    )}
                  >
                    Manage plan
                  </Button>
                )
              ))}
          </Box>
        </Flex>

        <ErrorMessage error={reactivatePurchaseError} />
        {extra}
        {!isCancellationPending && <LineItems mt="sm" />}
      </Card.Section>
      {!!footerElements.length && (
        <Card.Section px="lg" py="md" withBorder>
          {footerElements}
        </Card.Section>
      )}
    </Card>
  );
}

export function PurchaseDetailSection({
  purchase,
  extra,
}: {
  purchase: IPurchase;
  extra?: ReactNode;
}) {
  return (
    <PurchaseContextProvider purchase={purchase}>
      <PurchaseDetail extra={extra} />
    </PurchaseContextProvider>
  );
}
